import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  Avatar,
  makeStyles
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { PersonAddOutlined, Edit as EditIcon, Close } from '@material-ui/icons'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import CustomTextField from 'components/Form/CustomTextField'
import PasswordField from 'components/PasswordField'
import ProgressButton from 'components/ProgressButton'
import ConfirmModal from 'components/ConfirmationModal'
import { createOrganizer, updateOrganizer } from 'store/Organizers'

const notEmailTest = {
  name: 'not-email',
  message: 'Full Name cannot be an email address',
  test: (value) => !value || !/\S+@\S+\.\S+/.test(value)
}

const fullNameSchema = Yup.string()
  .required('Full Name is required')
  .test(notEmailTest)
  .matches(
    /^[\p{L}]+(?:[ .'-][\p{L}]+)*$/u,
    'Full Name must not contain numbers or special characters'
  )

const createSchema = Yup.object({
  fullName: fullNameSchema,
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .test(
      'password-strength',
      'Password must contain at least one letter and one number',
      (value) => /^(?=.*[A-Za-z])(?=.*\d)/.test(value)
    )
    .required('Password is required')
})

const editSchema = Yup.object({
  fullName: fullNameSchema,
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .test(
      'password-strength',
      'Password must contain at least one letter and one number',
      (value) => {
        if (!value) return true
        return /^(?=.*[A-Za-z])(?=.*\d)/.test(value)
      }
    )
})

const OrganizerFormModal = ({ open, onClose, organizer }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isEdit = Boolean(organizer)
  const [pendingPasswordUpdate, setPendingPasswordUpdate] = useState(null)
  const [confirmingPassword, setConfirmingPassword] = useState(false)

  const initialValues = isEdit
    ? { fullName: organizer.fullName || '', newPassword: '' }
    : { fullName: '', email: '', password: '' }

  const performSubmit = async (values, setSubmitting) => {
    if (isEdit) {
      const payload = { uid: organizer.uid, fullName: values.fullName.trim() }
      if (values.newPassword) payload.newPassword = values.newPassword
      await dispatch(updateOrganizer(payload))
    } else {
      const result = await dispatch(
        createOrganizer({
          fullName: values.fullName.trim(),
          email: values.email.trim().toLowerCase(),
          password: values.password
        })
      )
      if (result.error) {
        setSubmitting(false)
        return
      }
    }
    setSubmitting(false)
    onClose()
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    if (isEdit && values.newPassword) {
      // Password changes need explicit confirmation — defer to the dialog below.
      setSubmitting(false)
      setPendingPasswordUpdate(values)
      return
    }
    await performSubmit(values, setSubmitting)
  }

  const handleConfirmPasswordUpdate = async () => {
    if (!pendingPasswordUpdate) return
    setConfirmingPassword(true)
    await performSubmit(pendingPasswordUpdate, () => {})
    setConfirmingPassword(false)
    setPendingPasswordUpdate(null)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}>
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderLeft}>
          <Box className={classes.dialogIconWrap}>
            {isEdit ? (
              <EditIcon className={classes.dialogIcon} />
            ) : (
              <PersonAddOutlined className={classes.dialogIcon} />
            )}
          </Box>
          <Typography className={classes.dialogTitleText}>
            {isEdit ? 'Edit Conference Organizer' : 'Add Conference Organizer'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} className={classes.dialogCloseBtn}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Formik
        initialValues={initialValues}
        validationSchema={isEdit ? editSchema : createSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ values, setFieldValue, isSubmitting, touched, errors, handleBlur }) => (
          <Form>
            <DialogContent className={classes.dialogContent}>
              {isEdit && (
                <Box className={classes.identityCard}>
                  <Avatar className={classes.identityAvatar}>
                    {organizer?.fullName?.charAt(0)?.toUpperCase() ?? 'U'}
                  </Avatar>
                  <Box className={classes.identityText}>
                    <Typography className={classes.identityName}>{organizer?.fullName}</Typography>
                    <Typography className={classes.identityEmail}>{organizer?.email}</Typography>
                  </Box>
                </Box>
              )}
              {!isEdit && (
                <Alert severity="info" className={classes.guideNote}>
                  This organizer will be invited by email with the temporary password below and a
                  link to set their own password. They must complete that setup before they can log
                  in — the link expires after 48 hours.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CustomTextField name="fullName" label="Full Name" fullWidth variant="outlined" />
                </Grid>
                {!isEdit && (
                  <>
                    <Grid item xs={12}>
                      <CustomTextField
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="outlined"
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <PasswordField
                        name="password"
                        label="Temporary Password"
                        autoComplete="new-password"
                        formik={{
                          values,
                          handleChange: (e) => setFieldValue(e.target.name, e.target.value),
                          handleBlur,
                          touched,
                          errors
                        }}
                      />
                    </Grid>
                  </>
                )}
                {isEdit && (
                  <Grid item xs={12}>
                    <PasswordField
                      name="newPassword"
                      label="New Password (Optional)"
                      autoComplete="new-password"
                      disabled={organizer?.mustChangePassword}
                      formik={{
                        values,
                        handleChange: (e) => setFieldValue(e.target.name, e.target.value),
                        handleBlur,
                        touched,
                        errors
                      }}
                    />
                    {organizer?.mustChangePassword && (
                      <Alert severity="warning" className={classes.inlineWarning}>
                        This organizer hasn&apos;t completed account setup yet — password can&apos;t
                        be changed until then. Use &quot;Resend Activation Email&quot; from the list
                        instead.
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActionsBox}>
              <Button onClick={onClose} disabled={isSubmitting} color="default">
                Cancel
              </Button>
              <ProgressButton
                type="submit"
                variant="contained"
                color="primary"
                inProgress={isSubmitting}
                disabled={isSubmitting}
                label={isEdit ? 'Update Organizer' : 'Create Organizer'}
              />
            </DialogActions>
          </Form>
        )}
      </Formik>

      <ConfirmModal
        dialogTitle="Change Password"
        dialogText={`Are you sure you want to change the password for ${
          organizer?.fullName || 'this organizer'
        }? They will need to use the new password the next time they log in.`}
        open={Boolean(pendingPasswordUpdate)}
        inProgress={confirmingPassword}
        closeModal={() => setPendingPasswordUpdate(null)}
        handleConfirm={handleConfirmPasswordUpdate}
      />
    </Dialog>
  )
}

const useStyles = makeStyles({
  dialogPaper: {
    borderRadius: '16px'
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #eef0f3'
  },
  dialogHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0
  },
  dialogIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '10px',
    backgroundColor: 'rgba(21, 180, 219, 0.12)',
    flexShrink: 0
  },
  dialogIcon: {
    color: '#15b4db',
    fontSize: '22px'
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: '18px',
    color: '#1a2b3c'
  },
  dialogCloseBtn: {
    color: '#94a3b8'
  },
  dialogContent: {
    padding: '24px'
  },
  dialogActionsBox: {
    padding: '16px 24px',
    borderTop: '1px solid #eef0f3',
    gap: '8px'
  },
  guideNote: {
    marginBottom: '1rem'
  },
  identityCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 16px',
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: '1.5rem'
  },
  identityAvatar: {
    width: 44,
    height: 44,
    fontWeight: 700,
    fontSize: '1.1rem',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    flexShrink: 0
  },
  identityText: {
    flex: 1,
    minWidth: 0
  },
  identityName: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#0f172a'
  },
  identityEmail: {
    fontSize: '0.8rem',
    color: '#64748b'
  },
  inlineWarning: {
    marginTop: '0.75rem'
  }
})

export default OrganizerFormModal
